import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Upload, Download } from 'lucide-react';
import { calcInternalDue, internalDueColor, internalDueBadge, DEFAULT_BUFFERS } from '../utils/dueDate';
import { downloadExport } from '../utils/exportUtils';
import api from '../utils/api';
import usePersistedFilter from '../utils/usePersistedFilter';
import { fmtDate, fmtCurrency, FY_OPTIONS, CURRENT_FY, statusColor, canDelete, canImport } from '../utils/helpers';
import { PageHeader, DataTable, SearchBar, StatusBadge, Modal, FormField } from '../components/Table';
import DueDateEditModal from '../components/DueDateEditModal';
import ClientSearchDropdown from '../components/ClientSearchDropdown';
import ConfirmModal from '../components/ConfirmModal';
import ModuleImport from '../components/ModuleImport';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import BulkActionBar from '../components/BulkActionBar';
import BulkEmailModal from '../components/BulkEmailModal';
import WhatsAppModal from '../components/WhatsAppModal';

const AUDIT_TYPES = ['Statutory Audit', 'Tax Audit (44AB)', 'Internal Audit', 'Concurrent Audit', 'Stock Audit', 'GST Audit'];
const AUDIT_STATUSES = ['Engagement Letter Sent', 'Books Received', 'Fieldwork', 'Draft Report', 'Management Review', 'Final Report', 'Signed'];

export default function AuditPage() {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [fyFilter, setFyFilter] = usePersistedFilter('pms_filter_fy_audit');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [dueDateRecord, setDueDateRecord] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const canDel = canDelete(user);
  const canImp = canImport(user);
  const [buffers, setBuffers] = useState(DEFAULT_BUFFERS);
  useEffect(() => { api.get('/settings/buffer').then(r => setBuffers(b => ({ ...b, ...r.data }))).catch(() => {}); }, []);

  // Bulk actions (Feature 3)
  const [selected, setSelected] = useState(new Set());
  const [waTargets, setWaTargets] = useState(null);
  const [emailTargets, setEmailTargets] = useState(null);
  const toggleSel = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAllSel = rows => setSelected(s => s.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
  const selRows = () => filtered.filter(r => selected.has(r.id));
  const bulkUpdate = async updates => { try { await api.post('/audit/bulk-update', { ids: [...selected], updates }); toast.success('Updated'); setSelected(new Set()); load(); } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); } };
  const bulkDelete = async () => { try { await api.post('/audit/bulk-delete', { ids: [...selected] }); toast.success('Deleted'); setSelected(new Set()); load(); } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); } };
  const bulkTargets = () => { const seen = new Set(); return selRows().filter(r => { if (seen.has(r.client_id)) return false; seen.add(r.client_id); return true; }).map(r => ({ id: r.client_id, client_id: r.client_id, name: r.client_name, whatsapp: r.whatsapp, email: r.client_email })); };

  const load = () => {
    const params = {}; if (fyFilter) params.fy = fyFilter;
    if (showArchived) params.show_archived = '1';
    api.get('/audit', { params }).then(r => setData(r.data));
  };

  useEffect(() => {
    api.get('/auth/users').then(r => setStaff(r.data));
  }, []);
  useEffect(() => { load(); }, [fyFilter, showArchived]);
  // Alt+I — open the Add form ready to type
  useEffect(() => {
    const h = () => { setEditing(null); setForm({ fy: CURRENT_FY, status: 'Engagement Letter Sent' }); setShowModal(true); };
    window.addEventListener('pms:new-record', h);
    return () => window.removeEventListener('pms:new-record', h);
  }, []);

  const handleSave = async e => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/audit/${editing.id}`, form);
      else await api.post('/audit', form);
      toast.success('Saved'); setShowModal(false); setEditing(null); setForm({}); load();
    } catch (e) { toast.error(e?.response?.data?.error || e.message || 'Failed'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/audit/${deleteTarget.id}`); toast.success('Record deleted'); setDeleteTarget(null); load(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  const filtered = data.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    return d.client_name?.toLowerCase().includes(s) || d.client_code?.toLowerCase().includes(s);
  });

  const columns = [
    { key: 'client_name', label: 'Client', render: (v, row) => (
      <div><div className="font-medium text-gray-800 text-sm">{v}</div>
        {row.client_code && <div className="text-xs text-gray-400">{row.client_code}</div>}
      </div>
    )},
    { key: 'fy', label: 'FY' },
    { key: 'audit_type', label: 'Type' },
    { key: 'primary_assignee_name', label: 'Assigned To', render: (v, row) => (
      <span className="text-xs text-gray-600">{v || <span className="text-gray-300">—</span>}
        {row.secondary_assignee_name && <span className="text-gray-400"> / {row.secondary_assignee_name}</span>}
      </span>
    )},
    { key: 'effective_due_date', label: 'Due Date', render: (v, row) => {
      if (!v) return '—';
      const diff = (new Date(v) - new Date()) / 86400000;
      return (
        <div className="flex items-center gap-1.5">
          <span className={diff < 0 ? 'text-red-600 font-semibold' : diff <= 14 ? 'text-orange-600 font-semibold' : ''}>{fmtDate(v)}</span>
          {row.govt_extended ? <span className="bg-orange-100 text-orange-600 text-xs px-1 rounded font-medium">Govt Ext</span> : null}
          {row.revised_due_date && !row.govt_extended ? <span className="text-xs text-blue-500">Revised</span> : null}
          <button onClick={e => { e.stopPropagation(); setDueDateRecord(row); }} className="text-gray-300 hover:text-[#f0a500] transition-colors" title="Edit due date">
            <Pencil size={12} />
          </button>
        </div>
      );
    }},
    { key: 'status', label: 'Status', render: (v, row) => (
      <select value={v} onChange={async e => { await api.put(`/audit/${row.id}`, { ...row, status: e.target.value }); load(); }}
        className={`text-xs rounded-full px-2 py-0.5 border-0 cursor-pointer ${statusColor(v)}`}>
        {AUDIT_STATUSES.map(s => <option key={s}>{s}</option>)}
      </select>
    )},
    { key: 'fees_agreed', label: 'Fees Agreed', render: v => fmtCurrency(v) },
    { key: 'fees_received', label: 'Received', render: v => fmtCurrency(v) },
    { key: 'internal_due_date', label: 'Internal Due', render: (v, row) => v
      ? <span className={internalDueColor(v, row.status)}>{internalDueBadge(v, row.status)} {fmtDate(v)}</span>
      : <span className="text-gray-300">—</span>
    },
    { key: 'id', label: '', sortable: false, render: (v, row) => (
      <div className="flex gap-2 items-center">
        <button onClick={() => { setEditing(row); setForm(row); setShowModal(true); }} className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">Edit</button>
        <button title={row.is_archived ? 'Unarchive' : 'Archive'} onClick={async () => { await api.patch(`/audit/${row.id}/${row.is_archived ? 'unarchive' : 'archive'}`); toast.success(row.is_archived ? 'Unarchived' : 'Archived'); load(); }} className={`text-xs ${row.is_archived ? 'text-amber-600' : 'text-gray-300 hover:text-amber-600'}`}>{row.is_archived ? '↩' : '📁'}</button>
        {canDel && <button onClick={() => setDeleteTarget(row)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>}
      </div>
    )},
  ];

  return (
    <div>
      <PageHeader title="Audit" subtitle="Statutory & Tax Audit tracker"
        actions={
          <div className="flex gap-2">
            <button onClick={() => downloadExport('/export/audit', { fy: fyFilter }, `KUA_Audit_${new Date().toISOString().split('T')[0]}.xlsx`)} className="btn-secondary flex items-center gap-2 text-sm"><Download size={15} /> Export</button>
            {canImp && <button onClick={() => setShowImport(true)} className="btn-secondary flex items-center gap-2 text-sm"><Upload size={15} /> Import</button>}
            <button onClick={() => { setEditing(null); setForm({ fy: CURRENT_FY, status: 'Engagement Letter Sent' }); setShowModal(true); }} className="btn-primary flex items-center gap-2"><Plus size={16} /> Add Audit</button>
          </div>
        } />

      <div className="card">
        <div className="flex flex-wrap gap-3 mb-4">
          <SearchBar value={search} onChange={setSearch} placeholder="Search client..." />
          <select value={fyFilter} onChange={e => setFyFilter(e.target.value)} className="input-field w-32">
            <option value=''>All Years</option>{FY_OPTIONS.map(f => <option key={f}>{f}</option>)}
          </select>
          <button onClick={() => setShowArchived(a => !a)} className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${showArchived ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`}>{showArchived ? '📂 Archived' : '📁 Show Archived'}</button>
        </div>
                <BulkActionBar module="audit" count={selected.size} staff={staff}
          statuses={AUDIT_STATUSES}
          onAssign={id => bulkUpdate({ primary_assignee_id: id })}
          onStatus={s => bulkUpdate({ status: s })}
          onDelete={bulkDelete}
          onWA={() => setWaTargets(bulkTargets())}
          onEmail={() => setEmailTargets(bulkTargets())}
          onClear={() => setSelected(new Set())} />
        <DataTable columns={columns} data={filtered} selected={selected} onToggle={toggleSel} onToggleAll={toggleAllSel} />
      </div>

      {showModal && (
        <Modal title={editing ? 'Edit Audit' : 'Add Audit'} onClose={() => { setShowModal(false); setEditing(null); setForm({}); }}>
          <form onSubmit={handleSave} className="space-y-4">
            {!editing && <FormField label="Client" required>
              <ClientSearchDropdown
                value={form.client_id || ''}
                initialLabel=""
                service="Audit"
                onChange={(id) => setForm(f => ({ ...f, client_id: id }))}
                required
              />
            </FormField>}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="FY"><select value={form.fy||CURRENT_FY} onChange={e => setForm(f => ({...f, fy: e.target.value}))} className="input-field">{FY_OPTIONS.map(f => <option key={f}>{f}</option>)}</select></FormField>
              <FormField label="Audit Type"><select value={form.audit_type||''} onChange={e => setForm(f => ({...f, audit_type: e.target.value}))} className="input-field"><option value="">Select</option>{AUDIT_TYPES.map(t => <option key={t}>{t}</option>)}</select></FormField>
              <FormField label="Status"><select value={form.status||''} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="input-field">{AUDIT_STATUSES.map(s => <option key={s}>{s}</option>)}</select></FormField>
              <FormField label="Due Date"><input type="date" value={form.due_date||''} onChange={e => setForm(f => ({...f, due_date: e.target.value, internal_due_date: f.internal_due_date || calcInternalDue(e.target.value, buffers.buffer_audit)}))} className="input-field" /></FormField>
              <FormField label="Internal Due Date">
                <div className="flex gap-2">
                  <input type="date" value={form.internal_due_date||''} onChange={e => setForm(f => ({...f, internal_due_date: e.target.value}))} className="input-field flex-1" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, internal_due_date: calcInternalDue(f.due_date, buffers.buffer_audit) }))} className="text-xs border border-gray-200 rounded px-2 py-1.5 text-[#1e3a5f] hover:text-[#f0a500] whitespace-nowrap">Auto</button>
                </div>
              </FormField>
              <FormField label="Fees Agreed (₹)"><input type="number" value={form.fees_agreed||''} onChange={e => setForm(f => ({...f, fees_agreed: e.target.value}))} className="input-field" /></FormField>
              <FormField label="Fees Billed (₹)"><input type="number" value={form.fees_billed||''} onChange={e => setForm(f => ({...f, fees_billed: e.target.value}))} className="input-field" /></FormField>
              <FormField label="Fees Received (₹)"><input type="number" value={form.fees_received||''} onChange={e => setForm(f => ({...f, fees_received: e.target.value}))} className="input-field" /></FormField>
              <div></div>
              <FormField label="Primary Assignee">
                <select value={form.primary_assignee_id||''} onChange={e => setForm(f => ({...f, primary_assignee_id: e.target.value}))} className="input-field">
                  <option value="">Not assigned</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </FormField>
              <FormField label="Secondary Assignee">
                <select value={form.secondary_assignee_id||''} onChange={e => setForm(f => ({...f, secondary_assignee_id: e.target.value}))} className="input-field">
                  <option value="">None</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </FormField>
            </div>
            <FormField label="Notes"><textarea value={form.notes||''} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className="input-field" rows={2} /></FormField>
            <div className="flex gap-3"><button type="submit" className="btn-primary flex-1">Save</button><button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn-secondary flex-1">Cancel</button></div>
          </form>
        </Modal>
      )}

      {dueDateRecord && (
        <DueDateEditModal record={dueDateRecord} tableName="audits" moduleName="Audit" onClose={() => setDueDateRecord(null)} onSaved={load} />
      )}
      {waTargets && <WhatsAppModal targets={waTargets} defaultMsg={`Dear Client,

This is a message from Krishna Uday & Associates regarding your compliance work.

Regards,
KU Associates`} onClose={() => setWaTargets(null)} />}
      {emailTargets && <BulkEmailModal targets={emailTargets} defaultSubject="Update from Krishna Uday & Associates" onClose={() => setEmailTargets(null)} />}
      {deleteTarget && <ConfirmModal message="Delete this audit record? This cannot be undone." onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />}
      {showImport && <ModuleImport module="audit" onComplete={load} onClose={() => setShowImport(false)} />}
    </div>
  );
}
